import Card from '@/components/Card';
import { Button } from '@/components/Button';
import { Avatar } from '@/components/Avatar';
import { Input } from '@/components/Controls/Input';
import { Textarea } from '@/components/Controls/Textarea';
import { Switch } from '@/components/Controls/Switch';
import { SelectAutoComplete } from '@/components/Controls/SelectAutoComplete';
import { FormField } from '@/components/Form/FormField';
import { useEffect } from 'react';
import { FieldValues, Path, PathValue } from 'react-hook-form';
import { FormManagementProps } from '@/type/form.types';
import { TitleSection } from '@/components/Header/Section/TitleSection';
import { resetFieldError } from '@/utils/object.utils';
import { useGetMatchingEquipmentError } from '@/hooks/equipment.hooks';
import { FieldError } from '@/type/error.types';
import { Checkbox } from '@/components/Controls/Checkbox';

type EquipmentInfosManagementProps = {
  dataEquipmentOptions: { label: string; id: string }[];
};

export const EquipmentInfosManagement = <TSchema extends FieldValues>({
  t,
  data,
  watch,
  register,
  setValue,
  setError,
  errors,
  submitDisabled,
  onSubmit,
  control,
  dataEquipmentOptions,
}: FormManagementProps<TSchema> & Partial<EquipmentInfosManagementProps>) => {
  const nameValue = watch && watch('name' as Path<TSchema>);

  const isMultipleValue =
    watch && watch('isMultiple' as Path<TSchema>, data?.isMultiple ?? false);
  const isFrontPartValue =
    watch && watch('isFrontPart' as Path<TSchema>, data?.isFrontPart ?? false);
  const isBackPartValue =
    watch && watch('isBackPart' as Path<TSchema>, data?.isBackPart ?? false);
  const isLeftPartValue =
    watch && watch('isLeftPart' as Path<TSchema>, data?.isLeftPart ?? false);
  const isRightPartValue =
    watch && watch('isRightPart' as Path<TSchema>, data?.isRightPart ?? false);

  const activeValue = watch && watch('active' as Path<TSchema>);

  const { data: matchingEquipmentError } = useGetMatchingEquipmentError({
    id: data?.id,
    name: nameValue,
  });

  useEffect(() => {
    if (nameValue) {
      resetFieldError<TSchema>({ fieldName: 'name', setError });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameValue]);

  useEffect(() => {
    if (isFrontPartValue) {
      setValue(
        'frontPartTranslation' as Path<TSchema>,
        'none' as PathValue<TSchema, Path<TSchema>>,
      );
    }
    if (isBackPartValue) {
      setValue(
        'backPartTranslation' as Path<TSchema>,
        'none' as PathValue<TSchema, Path<TSchema>>,
      );
    }
    if (isLeftPartValue) {
      setValue(
        'leftPartTranslation' as Path<TSchema>,
        'none' as PathValue<TSchema, Path<TSchema>>,
      );
    }
    if (isRightPartValue) {
      setValue(
        'rightPartTranslation' as Path<TSchema>,
        'none' as PathValue<TSchema, Path<TSchema>>,
      );
    }
  }, [
    isFrontPartValue,
    isBackPartValue,
    isLeftPartValue,
    isRightPartValue,
    setValue,
  ]);

  useEffect(() => {
    if (!isMultipleValue) {
      setValue('maxTimes' as Path<TSchema>, null);
    }
  }, [isMultipleValue, setValue]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const equipmentErrorMessages = matchingEquipmentError?.data?.errorMessages;
    if (equipmentErrorMessages?.includes(FieldError.NAME_EXISTS)) {
      setError('name' as Path<TSchema>, {
        message: t('equipmentNameAlreadyExists'),
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    onSubmit?.();
  };

  return (
    <form onSubmit={handleSubmit}>
      <Card
        title={
          <TitleSection
            text={t('theEquipment')}
            defaultIconClassName="ki-badge"
          />
        }
        footer={
          <Button
            buttonClassName="px-8"
            text={
              submitDisabled ? `${t('common:loading')}...` : t('common:save')
            }
            disabled={submitDisabled}
          />
        }
        bodyClassName="py-8 flex flex-col gap-5"
      >
        <FormField
          text={t('common:icon')}
          containerClassName="mb-2 max-md:mb-0"
          textWrapperClassName="mt-3 max-md:mt-0"
        >
          <Avatar
            outerContainerClassName="max-md:w-full"
            containerClassName="max-md:mx-auto"
            imageUrl={data?.icon}
            onChange={(url) =>
              setValue?.(
                'icon' as Path<TSchema>,
                url as PathValue<TSchema, Path<TSchema>>,
              )
            }
          />
        </FormField>
        <FormField
          text={t('equipmentName')}
          required
          textWrapperClassName="mt-3"
        >
          <div className="flex flex-1 flex-wrap items-start gap-5">
            <Input
              {...(register && register('name' as Path<TSchema>))}
              errorMessage={errors && (errors.name?.message as string)}
              placeholder={t('equipmentName')}
              type="text"
            />
          </div>
        </FormField>

        <FormField
          containerClassName="my-4.5"
          textWrapperClassName="mt-3"
          text={t('common:vehiclePart')}
        >
          <div className="flex flex-wrap items-start gap-5 mt-3 max-sm:gap-2.5">
            <Checkbox
              {...(register && register('isFrontPart' as Path<TSchema>))}
              text={t('vehicule:partFront')}
              containerClassName="max-sm:flex-none max-sm:w-full"
            />
            <Checkbox
              {...(register && register('isBackPart' as Path<TSchema>))}
              text={t('vehicule:partBack')}
              containerClassName="max-sm:flex-none max-sm:w-full"
            />
            <Checkbox
              {...(register && register('isLeftPart' as Path<TSchema>))}
              text={t('vehicule:partLeft')}
              containerClassName="max-sm:flex-none max-sm:w-full"
            />
            <Checkbox
              {...(register && register('isRightPart' as Path<TSchema>))}
              text={t('vehicule:partRight')}
              containerClassName="max-sm:flex-none max-sm:w-full"
            />
          </div>
        </FormField>

        <FormField
          containerClassName="mb-8"
          textWrapperClassName="mt-4"
          text={t('common:automaticTranslation')}
          subText={t('IfFoundOnAnotherPartOfTheVehicle')}
        >
          <div className="flex flex-col w-[350px] items-start gap-4.5 mt-3 max-sm:w-full">
            <SelectAutoComplete
              containerClassName="max-sm:flex-none max-sm:gap-1"
              autocompleteClassName="max-sm:flex-none max-sm:min-w-0 max-sm:w-full"
              labelClassName="min-w-[100px] max-sm:min-w-0"
              label="Avant"
              name="frontPartTranslation"
              control={control}
              options={dataEquipmentOptions ?? []}
              disabled={isFrontPartValue}
            />
            <SelectAutoComplete
              containerClassName="max-sm:flex-none max-sm:gap-1"
              autocompleteClassName="max-sm:flex-none max-sm:min-w-0 max-sm:w-full"
              labelClassName="min-w-[100px] max-sm:min-w-0"
              label="Arrière"
              name="backPartTranslation"
              control={control}
              options={dataEquipmentOptions ?? []}
              disabled={isBackPartValue}
            />
            <SelectAutoComplete
              containerClassName="max-sm:flex-none max-sm:gap-1"
              autocompleteClassName="max-sm:flex-none max-sm:min-w-0 max-sm:w-full"
              labelClassName="min-w-[100px] max-sm:min-w-0"
              label="Gauche"
              name="leftPartTranslation"
              control={control}
              options={dataEquipmentOptions ?? []}
              disabled={isLeftPartValue}
            />
            <SelectAutoComplete
              containerClassName="max-sm:flex-none max-sm:gap-1"
              autocompleteClassName="max-sm:flex-none max-sm:min-w-0 max-sm:w-full"
              labelClassName="min-w-[100px] max-sm:min-w-0"
              label="Droite"
              name="rightPartTranslation"
              control={control}
              options={dataEquipmentOptions ?? []}
              disabled={isRightPartValue}
            />
          </div>
        </FormField>
        <FormField text={t('common:comment')} textWrapperClassName="mt-3">
          <div className="flex flex-1 flex-wrap items-start gap-5">
            <Textarea
              {...(register && register('comment' as Path<TSchema>))}
              errorMessage={errors && (errors.comment?.message as string)}
              placeholder={t('common:comment')}
            />
          </div>
        </FormField>
        <FormField
          containerClassName="mt-4.5"
          textWrapperClassName="mt-2"
          text={`${t('common:multiple')} ?`}
          subText={t('canAppearNtimesOnCheckPoint')}
        >
          <div className="flex flex-1 flex-wrap items-start gap-5 mt-2">
            <Switch
              {...(register && register('isMultiple' as Path<TSchema>))}
              errorMessage={errors && (errors.isMultiple?.message as string)}
              checked={isMultipleValue}
            />
          </div>
        </FormField>
        {isMultipleValue && (
          <FormField
            text={t('common:maxNumberOfTimes')}
            required
            textWrapperClassName="mt-2"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register && register('maxTimes' as Path<TSchema>))}
                errorMessage={errors && (errors.maxTimes?.message as string)}
                placeholder={t('common:maxTimes')}
                onInput={(e) => {
                  // Enforce to input only a positive number
                  e.currentTarget.value =
                    Number(e.currentTarget.value) > 0
                      ? e.currentTarget.value
                      : null;
                }}
                type="number"
              />
            </div>
          </FormField>
        )}
        <FormField text={t('common:activeStatus')} textWrapperClassName="mt-3">
          <div className="flex flex-1 flex-wrap items-start gap-5 mt-3">
            <Switch
              {...(register && register('active' as Path<TSchema>))}
              errorMessage={errors && (errors.active?.message as string)}
              checked={data ? activeValue : true}
            />
          </div>
        </FormField>
      </Card>
    </form>
  );
};
